// Sample responses for testing UI when backend is unavailable
// Based on IDYA Specification v3.1 - January 2026

// Response type detection
export const detectResponseType = (response) => {
  if (response.dashboard) {
    return 'DASHBOARD';
  } else if (response.diagnostic) {
    return 'DIAGNOSTIC';
  } else if (response.timeline) {
    return 'TIMELINE';
  } else if (response.chart) {
    return 'CHART';
  } else if (response.table) {
    return 'TABLE';
  } else if (response.wizard) {
    return 'WIZARD';
  } else if (response.fileSearchResults) {
    return 'FILE_SEARCH';
  } else if (response.actions && response.actions.length > 0) {
    return 'ACTIONS';
  } else if (response.reply && response.rating) {
    return 'STRUCTURED';
  } else {
    return 'PLAIN';
  }
};

// Chart color palette
export const chartColors = {
  primary: '#22c55e',   // Green (brand)
  blue: '#3b82f6',
  yellow: '#f59e0b',
  red: '#ef4444',
  green: '#22c55e',
  gray: '#71717a',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
};

// Trend indicator colors
export const trendColors = {
  UP: '#22c55e',    // Green - positive
  DOWN: '#ef4444',  // Red - negative
  FLAT: '#71717a',  // Gray - neutral
};

// Get chart color by name
export const getChartColor = (colorName) => {
  return chartColors[colorName] || chartColors.gray;
};

// Status color mapping for TABLE columns
export const getStatusColor = (status, statusMapping) => {
  if (statusMapping && statusMapping[status]) {
    const colorMap = {
      green: '#22c55e',
      yellow: '#f59e0b',
      orange: '#f97316', // On Hold showed gray: the colour was not in this map (FH-318)
      red: '#ef4444',
      blue: '#3b82f6',
      gray: '#71717a',
    };
    return colorMap[statusMapping[status].color] || '#71717a';
  }
  return '#71717a';
};

// Highlight color mapping for TABLE rows
export const getHighlightColor = (highlightColor) => {
  const colorMap = {
    info: 'rgba(59, 130, 246, 0.1)',
    success: 'rgba(34, 197, 94, 0.1)',
    warning: 'rgba(245, 158, 11, 0.1)',
    error: 'rgba(239, 68, 68, 0.1)',
  };
  return colorMap[highlightColor] || 'transparent';
};

// Check if action is enabled based on selection count
export const isActionEnabled = (action, selectedCount) => {
  const mode = action.selectionMode || 'ONE_OR_MORE';
  const min = action.minSelection || 1;
  const max = action.maxSelection || Infinity;
  const exact = action.selectionCount;

  switch (mode) {
    case 'SINGLE':
      return selectedCount === 1;
    case 'ONE_OR_MORE':
      return selectedCount >= min && selectedCount <= max;
    case 'MULTIPLE':
      return selectedCount >= 2 && selectedCount <= max;
    case 'EXACT':
      return selectedCount === exact;
    case 'NONE':
      return true;
    default:
      return selectedCount >= min && selectedCount <= max;
  }
};

// Get disabled reason for action
export const getDisabledReason = (action, selectedCount) => {
  const mode = action.selectionMode || 'ONE_OR_MORE';
  if (mode === 'MULTIPLE' && selectedCount < 2) {
    return 'Select at least 2 items';
  }
  if (mode === 'EXACT') {
    return `Select exactly ${action.selectionCount} items`;
  }
  if (selectedCount < (action.minSelection || 1)) {
    return `Select at least ${action.minSelection || 1} items`;
  }
  if (action.maxSelection && selectedCount > action.maxSelection) {
    return `Select at most ${action.maxSelection} items`;
  }
  return null;
};

// Substitute template variables in action sentence
export const substituteTemplates = (template, rowData, selectedIds = []) => {
  if (!template) return '';
  let result = template;

  // Replace row data fields
  if (rowData) {
    Object.keys(rowData).forEach(key => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), rowData[key] || '');
    });
  }

  // Replace selected_ids
  result = result.replace(/{{selected_ids}}/g, selectedIds.join(','));
  result = result.replace(/{{selected_count}}/g, selectedIds.length.toString());
  result = result.replace(/{{today}}/g, new Date().toISOString().split('T')[0]);

  return result;
};

// Rating color mapping
export const getRatingColor = (rating) => {
  const colors = {
    CRITICAL: '#dc3545',
    HIGH: '#f7931e',
    WATCH: '#6c757d',
    NORMAL: '#22c55e',
  };
  return colors[rating] || colors.NORMAL;
};

// Timeline event icon color mapping
export const getEventIconColor = (colorName) => {
  const colorMap = {
    green: '#22c55e',
    blue: '#3b82f6',
    yellow: '#f59e0b',
    red: '#ef4444',
    gray: '#71717a',
    purple: '#8b5cf6',
    cyan: '#06b6d4',
    primary: '#22c55e',
  };
  return colorMap[colorName] || colorMap.gray;
};

// Timeline duration alert color mapping
export const getDurationAlertColor = (alertType) => {
  const colorMap = {
    info: { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', text: '#3b82f6' },
    warning: { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', text: '#f59e0b' },
    error: { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#ef4444' },
  };
  return colorMap[alertType] || null;
};

// Diagnostic rule status color mapping
export const getDiagnosticStatusColor = (status) => {
  const colorMap = {
    PASSED: { bg: 'rgba(34, 197, 94, 0.1)', border: '#22c55e', text: '#22c55e', icon: '✓' },
    FAILED: { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#ef4444', icon: '✗' },
    WARNING: { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', text: '#f59e0b', icon: '⚠' },
    SKIPPED: { bg: 'rgba(113, 113, 122, 0.1)', border: '#71717a', text: '#71717a', icon: '○' },
  };
  return colorMap[status] || colorMap.SKIPPED;
};

// Diagnostic item status color mapping
export const getDiagnosticItemStatusColor = (status) => {
  const colorMap = {
    PASSED: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
    FAILED: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444' },
    WARNING: { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b' },
    PARTIAL: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
  };
  return colorMap[status] || colorMap.PASSED;
};

// ============================================================================
// DRIVE/FILE Response Schema
// ============================================================================

// Initial drive data structure for lazy loading - only root folder loaded
export const sampleDriveDataLazy = {
  driveId: "drive-user-001",
  title: "My Files",
  rootFolderId: "folder-root",
  usage: { used: "245 MB", total: "5 GB", percentage: 4.9 },
  items: [
    {
      id: "folder-root",
      name: "My Files",
      type: "FOLDER",
      parentId: null,
      folderPath: "MyFiles",
      children: ["folder-reports", "folder-imports", "folder-exports", "file-001"],
      hasChildren: true,
      isLoaded: true,  // Root is pre-loaded
      isLoading: false,
      createdAt: "2026-01-01T00:00:00Z",
      modifiedAt: "2026-01-20T10:30:00Z"
    },
    // Direct children of root - folders not yet loaded
    {
      id: "folder-reports",
      name: "Reports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Reports",
      children: [],
      hasChildren: true,  // Backend tells us it has children
      isLoaded: false,    // Not loaded yet
      isLoading: false,
      createdAt: "2026-01-05T08:00:00Z",
      modifiedAt: "2026-01-19T14:20:00Z"
    },
    {
      id: "folder-imports",
      name: "Imports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Imports",
      children: [],
      hasChildren: true,
      isLoaded: false,
      isLoading: false,
      createdAt: "2026-01-02T10:00:00Z",
      modifiedAt: "2026-01-17T09:30:00Z"
    },
    {
      id: "folder-exports",
      name: "Exports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Exports",
      children: [],
      hasChildren: true,
      isLoaded: false,
      isLoading: false,
      createdAt: "2026-01-03T11:00:00Z",
      modifiedAt: "2026-01-16T15:45:00Z"
    },
    // File in root folder
    {
      id: "file-001",
      name: "inventory-export.csv",
      type: "FILE",
      parentId: "folder-root",
      size: 2457600,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-001/download",
      createdAt: "2026-01-15T13:00:00Z",
      modifiedAt: "2026-01-15T13:00:00Z"
    },
  ]
};

// Sample folder contents data - simulates what backend returns when loading a folder
export const sampleFolderContents = {
  "MyFiles/Reports": {
    folders: [
      { name: "Daily", path: "MyFiles/Reports/Daily", hasChildren: true },
      { name: "Weekly", path: "MyFiles/Reports/Weekly", hasChildren: true },
    ],
    files: [
      { s3Key: "file-002", name: "summary-report.pdf", size: 1048576, mimeType: "application/pdf", lastModified: "2026-01-12T10:30:00Z" },
    ]
  },
  "MyFiles/Reports/Daily": {
    folders: [],
    files: [
      { s3Key: "file-003", name: "jan-20-report.csv", size: 1228800, mimeType: "text/csv", lastModified: "2026-01-20T08:00:00Z" },
      { s3Key: "file-004", name: "jan-19-report.csv", size: 1126400, mimeType: "text/csv", lastModified: "2026-01-19T08:00:00Z" },
      { s3Key: "file-005", name: "jan-18-report.csv", size: 1003520, mimeType: "text/csv", lastModified: "2026-01-18T08:00:00Z" },
    ]
  },
  "MyFiles/Reports/Weekly": {
    folders: [],
    files: [
      { s3Key: "file-006", name: "week-3-summary.xlsx", size: 524288, mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", lastModified: "2026-01-18T12:00:00Z" },
    ]
  },
  "MyFiles/Imports": {
    folders: [],
    files: [
      { s3Key: "file-007", name: "vendor-catalog.csv", size: 3145728, mimeType: "text/csv", lastModified: "2026-01-10T09:00:00Z" },
      { s3Key: "file-008", name: "sku-mapping.json", size: 204800, mimeType: "application/json", lastModified: "2026-01-17T09:30:00Z" },
    ]
  },
  "MyFiles/Exports": {
    folders: [],
    files: [
      { s3Key: "file-009", name: "inventory-backup.zip", size: 10485760, mimeType: "application/zip", lastModified: "2026-01-16T15:45:00Z" },
    ]
  }
};

/**
 * Transform folder contents response into items to add to the drive data
 * @param {Object} folderContents - { folders: [...], files: [...] }
 * @param {string} parentFolderId - The ID of the parent folder
 * @param {string} parentFolderPath - The path of the parent folder
 * @returns {Array} Array of items to add to driveData.items
 */
export const transformFolderContents = (folderContents, parentFolderId, parentFolderPath) => {
  const items = [];

  // Transform folders
  folderContents.folders?.forEach(folder => {
    items.push({
      id: `folder-${folder.path.replace(/[\\\/]/g, '-')}`,
      name: folder.name,
      type: 'FOLDER',
      parentId: parentFolderId,
      folderPath: folder.path,
      children: [],
      hasChildren: folder.hasChildren,
      isLoaded: false,
      isLoading: false,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    });
  });

  // Transform files
  folderContents.files?.forEach(file => {
    // Skip placeholder files
    if (file.name === FOLDER_PLACEHOLDER_FILE) return;

    items.push({
      id: file.s3Key,
      name: file.name,
      type: 'FILE',
      parentId: parentFolderId,
      size: file.size || 0,
      mimeType: file.mimeType || getMimeTypeFromFileName(file.name),
      downloadUrl: `/api/files/${encodeURIComponent(file.s3Key)}/download`,
      createdAt: file.lastModified || new Date().toISOString(),
      modifiedAt: file.lastModified || new Date().toISOString(),
    });
  });

  return items;
};

// Full sample drive data (for reference/testing with eager loading)
export const sampleDriveData = {
  driveId: "drive-user-001",
  title: "My Files",
  rootFolderId: "folder-root",
  usage: { used: "245 MB", total: "5 GB", percentage: 4.9 },
  items: [
    {
      id: "folder-root",
      name: "My Files",
      type: "FOLDER",
      parentId: null,
      folderPath: "MyFiles",
      children: ["folder-reports", "folder-imports", "folder-exports", "file-001"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-01T00:00:00Z",
      modifiedAt: "2026-01-20T10:30:00Z"
    },
    {
      id: "folder-reports",
      name: "Reports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Reports",
      children: ["folder-daily", "folder-weekly", "file-002"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-05T08:00:00Z",
      modifiedAt: "2026-01-19T14:20:00Z"
    },
    {
      id: "folder-daily",
      name: "Daily",
      type: "FOLDER",
      parentId: "folder-reports",
      folderPath: "MyFiles/Reports/Daily",
      children: ["file-003", "file-004", "file-005"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-10T09:00:00Z",
      modifiedAt: "2026-01-20T16:00:00Z"
    },
    {
      id: "folder-weekly",
      name: "Weekly",
      type: "FOLDER",
      parentId: "folder-reports",
      folderPath: "MyFiles/Reports/Weekly",
      children: ["file-006"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-10T09:00:00Z",
      modifiedAt: "2026-01-18T12:00:00Z"
    },
    {
      id: "folder-imports",
      name: "Imports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Imports",
      children: ["file-007", "file-008"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-02T10:00:00Z",
      modifiedAt: "2026-01-17T09:30:00Z"
    },
    {
      id: "folder-exports",
      name: "Exports",
      type: "FOLDER",
      parentId: "folder-root",
      folderPath: "MyFiles/Exports",
      children: ["file-009"],
      hasChildren: true,
      isLoaded: true,
      isLoading: false,
      createdAt: "2026-01-03T11:00:00Z",
      modifiedAt: "2026-01-16T15:45:00Z"
    },
    {
      id: "file-001",
      name: "inventory-export.csv",
      type: "FILE",
      parentId: "folder-root",
      size: 2457600,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-001/download",
      createdAt: "2026-01-15T13:00:00Z",
      modifiedAt: "2026-01-15T13:00:00Z"
    },
    {
      id: "file-002",
      name: "summary-report.pdf",
      type: "FILE",
      parentId: "folder-reports",
      size: 1048576,
      mimeType: "application/pdf",
      downloadUrl: "/api/files/file-002/download",
      createdAt: "2026-01-12T10:30:00Z",
      modifiedAt: "2026-01-12T10:30:00Z"
    },
    {
      id: "file-003",
      name: "jan-20-report.csv",
      type: "FILE",
      parentId: "folder-daily",
      size: 1228800,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-003/download",
      createdAt: "2026-01-20T08:00:00Z",
      modifiedAt: "2026-01-20T08:00:00Z"
    },
    {
      id: "file-004",
      name: "jan-19-report.csv",
      type: "FILE",
      parentId: "folder-daily",
      size: 1126400,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-004/download",
      createdAt: "2026-01-19T08:00:00Z",
      modifiedAt: "2026-01-19T08:00:00Z"
    },
    {
      id: "file-005",
      name: "jan-18-report.csv",
      type: "FILE",
      parentId: "folder-daily",
      size: 1003520,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-005/download",
      createdAt: "2026-01-18T08:00:00Z",
      modifiedAt: "2026-01-18T08:00:00Z"
    },
    {
      id: "file-006",
      name: "week-3-summary.xlsx",
      type: "FILE",
      parentId: "folder-weekly",
      size: 524288,
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      downloadUrl: "/api/files/file-006/download",
      createdAt: "2026-01-18T12:00:00Z",
      modifiedAt: "2026-01-18T12:00:00Z"
    },
    {
      id: "file-007",
      name: "vendor-catalog.csv",
      type: "FILE",
      parentId: "folder-imports",
      size: 3145728,
      mimeType: "text/csv",
      downloadUrl: "/api/files/file-007/download",
      createdAt: "2026-01-10T09:00:00Z",
      modifiedAt: "2026-01-10T09:00:00Z"
    },
    {
      id: "file-008",
      name: "sku-mapping.json",
      type: "FILE",
      parentId: "folder-imports",
      size: 204800,
      mimeType: "application/json",
      downloadUrl: "/api/files/file-008/download",
      createdAt: "2026-01-17T09:30:00Z",
      modifiedAt: "2026-01-17T09:30:00Z"
    },
    {
      id: "file-009",
      name: "inventory-backup.zip",
      type: "FILE",
      parentId: "folder-exports",
      size: 10485760,
      mimeType: "application/zip",
      downloadUrl: "/api/files/file-009/download",
      createdAt: "2026-01-16T15:45:00Z",
      modifiedAt: "2026-01-16T15:45:00Z"
    }
  ]
};

// Helper function to build folder tree from flat items array
export const buildFolderTree = (items) => {
  const itemMap = {};
  items.forEach(item => {
    itemMap[item.id] = { ...item, childNodes: [] };
  });

  const tree = [];
  items.forEach(item => {
    if (item.parentId === null) {
      tree.push(itemMap[item.id]);
    } else if (itemMap[item.parentId]) {
      itemMap[item.parentId].childNodes.push(itemMap[item.id]);
    }
  });

  return tree;
};

// Helper function to get file icon based on mime type
export const getFileIcon = (mimeType) => {
  if (!mimeType) return 'file';

  if (mimeType.includes('csv') || mimeType.includes('spreadsheet')) return 'csv';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('json')) return 'json';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'zip';
  if (mimeType.includes('image')) return 'image';
  if (mimeType.includes('text')) return 'text';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'doc';

  return 'file';
};

// Helper function to format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  if (!bytes) return '-';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper function to format date for display
export const formatFileDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper function to get folder contents (files and subfolders)
export const getFolderContents = (items, folderId) => {
  return items.filter(item => item.parentId === folderId);
};

// Helper function to get breadcrumb path for a folder
export const getBreadcrumbPath = (items, folderId) => {
  const path = [];
  let currentId = folderId;

  while (currentId) {
    const item = items.find(i => i.id === currentId);
    if (item) {
      path.unshift(item);
      currentId = item.parentId;
    } else {
      break;
    }
  }

  return path;
};

// ============================================================================
// S3 Data Transformation
// ============================================================================

/**
 * Derive MIME type from filename extension
 * @param {string} fileName - The file name
 * @returns {string} MIME type
 */
const getMimeTypeFromFileName = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const mimeMap = {
    'txt': 'text/plain',
    'csv': 'text/csv',
    'json': 'application/json',
    'pdf': 'application/pdf',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xls': 'application/vnd.ms-excel',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'doc': 'application/msword',
    'zip': 'application/zip',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'html': 'text/html',
    'xml': 'application/xml',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'ppt': 'application/vnd.ms-powerpoint',
  };
  return mimeMap[ext] || 'application/octet-stream';
};

/**
 * Parse a file path into folder path array and file name
 * @param {string} filePath - Path like "folder1\folder12\file3.txt" or "folder1/folder12/file3.txt"
 * @returns {{ fileName: string, folderPath: string[] }}
 */
const parsePath = (filePath) => {
  // Support both \ and / separators
  const parts = filePath.split(/[\\\/]/);
  const fileName = parts.pop();
  const folderPath = parts; // Array of folder names from root to parent
  return { fileName, folderPath };
};

// Placeholder file name used to represent empty folders in S3
export const FOLDER_PLACEHOLDER_FILE = '.folder';

/**
 * Transform flat S3 file listing into DriveExplorer data structure
 * @param {Array} s3Files - Array of { s3Key, filePath, size?, lastModified? }
 * @param {Object} options - { title?, driveId?, usageInfo? }
 * @returns {Object} DriveExplorer-compatible data structure
 */
export const transformS3DataToDriveStructure = (s3Files, options = {}) => {
  if (!s3Files || s3Files.length === 0) {
    return {
      driveId: options.driveId || `drive-${Date.now()}`,
      title: options.title || 'My Files',
      rootFolderId: 'folder-root',
      usage: options.usageInfo || { used: '0 MB', total: '5 GB', percentage: 0 },
      items: [{
        id: 'folder-root',
        name: options.title || 'My Files',
        type: 'FOLDER',
        parentId: null,
        folderPath: '', // Root has empty path
        children: [],
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      }],
    };
  }

  const items = [];
  const folderMap = new Map(); // path -> folderId mapping
  const folderPathMap = new Map(); // folderId -> actual S3 path
  const folderChildren = new Map(); // folderId -> children array

  // Determine root folder name from first file path
  const firstPath = s3Files[0]?.filePath || 'MyFiles';
  const rootName = firstPath.split(/[\\\/]/)[0] || options.title || 'My Files';
  const rootId = 'folder-root';

  // Initialize root folder tracking
  folderMap.set('', rootId);
  folderPathMap.set(rootId, '');
  folderChildren.set(rootId, []);

  // Always add the root folder item
  items.push({
    id: rootId,
    name: options.title || 'My Files',
    type: 'FOLDER',
    parentId: null,
    folderPath: '',
    children: [], // Will be populated later
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
  });

  // Process each S3 file
  s3Files.forEach(({ s3Key, filePath, size = 0, lastModified, docId, title }) => {
    const { fileName, folderPath } = parsePath(filePath);

    // Skip placeholder files - they're just markers for empty folders
    const isPlaceholder = fileName === FOLDER_PLACEHOLDER_FILE ||
                          fileName.startsWith('.');

    // Create all folders in the path if they don't exist
    let currentPath = '';
    let parentId = null;

    folderPath.forEach((folderName, index) => {
      const prevPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

      if (!folderMap.has(currentPath)) {
        // Generate folder ID based on path (all folders are children of root or other folders)
        const folderId = `folder-${currentPath.replace(/[\\\/]/g, '-')}`;
        // Parent: root folder for first level, otherwise get from previous path
        const parentFolderId = index === 0 ? rootId : folderMap.get(prevPath);

        // Create folder item with folderPath for backend communication
        const folderItem = {
          id: folderId,
          name: folderName,
          type: 'FOLDER',
          parentId: parentFolderId,
          folderPath: currentPath, // Store actual S3 path for backend calls
          children: [], // Will be populated later
          createdAt: new Date().toISOString(),
          modifiedAt: lastModified || new Date().toISOString(),
        };
        items.push(folderItem);

        folderMap.set(currentPath, folderId);
        folderPathMap.set(folderId, currentPath);
        folderChildren.set(folderId, []);

        // Add folder to parent's children
        if (parentFolderId && folderChildren.has(parentFolderId)) {
          folderChildren.get(parentFolderId).push(folderId);
        }
      }
      parentId = folderMap.get(currentPath);
    });

    // Only create file item if it's not a placeholder
    if (!isPlaceholder) {
      // If no folder path, file is at root level - set parentId to root folder
      const effectiveParentId = parentId || rootId;

      const fileItem = {
        id: s3Key,
        name: fileName,
        type: 'FILE',
        parentId: effectiveParentId,
        size: size,
        mimeType: getMimeTypeFromFileName(fileName),
        // Use docId for download URL since the backend endpoint uses docId
        downloadUrl: docId ? `http://localhost:8082/api/v1/documents/${encodeURIComponent(docId)}/download` : null,
        createdAt: lastModified || new Date().toISOString(),
        modifiedAt: lastModified || new Date().toISOString(),
        docId: docId, // Preserve docId for delete operations
        title: title,
        s3Key: s3Key, // Preserve s3Key for reference
      };
      items.push(fileItem);

      // Add file to parent folder's children
      if (folderChildren.has(effectiveParentId)) {
        folderChildren.get(effectiveParentId).push(s3Key);
      }
    }
  });

  // Update children arrays for all folders
  items.forEach(item => {
    if (item.type === 'FOLDER' && folderChildren.has(item.id)) {
      item.children = folderChildren.get(item.id);
    }
  });

  // Calculate total size for usage info (exclude placeholder files)
  const totalBytes = s3Files
    .filter(file => {
      const fileName = file.filePath.split(/[\\\/]/).pop() || '';
      return !fileName.startsWith('.');
    })
    .reduce((sum, file) => sum + (file.size || 0), 0);
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return {
    driveId: options.driveId || `drive-${Date.now()}`,
    title: options.title || rootName,
    rootFolderId: rootId,
    usage: options.usageInfo || {
      used: formatBytes(totalBytes),
      total: '5 GB',
      percentage: parseFloat(((totalBytes / (5 * 1024 * 1024 * 1024)) * 100).toFixed(2)),
    },
    items: items,
  };
};

// Get response text for display (handles both PLAIN and STRUCTURED)
export const getResponseText = (response) => {
  const type = detectResponseType(response);
  if (type === 'PLAIN') {
    return response.response;
  } else {
    return response.reply;
  }
};
